/**
 * CheckinWorkflowDrawer — multi-step guided check-in flow.
 *
 * Steps:
 *   1. Vaccine check — list all pets with vaccine status, warn on issues.
 *   2. Customer details — verify name, phone, emergency contact.
 *   3. Record belongings — add items brought by the customer.
 *   4. Confirm & check in — summary then POST /staff/bookings/:id/checkin.
 *
 * Used from BookingDetail > PetSection when staff click "Check in".
 * Replaces the previous direct `staffCheckinBooking(id)` call.
 */
import {
  type BookingGroupCustomer,
  type BookingGroupEntry,
  type BookingGroupVaccination,
  createBookingBelonging,
  staffCheckinBooking,
} from '@/lib/api';
import {
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Loader2,
  LogIn,
  Package,
  Plus,
  Syringe,
  Trash2,
  User,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { createPortal } from 'react-dom';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CheckinWorkflowDrawerProps {
  bookingId: string;
  entry: BookingGroupEntry;
  customer: BookingGroupCustomer | null;
  /** Reserved for future steps that show pricing. */
  currency?: string;
  onClose: () => void;
  onCheckedIn: () => void;
}

interface BelongingRow {
  id: string; // client-only key for list rendering
  description: string;
}

// ---------------------------------------------------------------------------
// Step labels
// ---------------------------------------------------------------------------

const STEPS = ['Vaccine Check', 'Customer Details', 'Record Belongings', 'Confirm & Check In'];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: total }, (_, i) => (
        <div
          key={i}
          className={`h-1.5 flex-1 rounded-full transition-colors ${
            i < current ? 'bg-emerald-500' : i === current ? 'bg-blue-600' : 'bg-border'
          }`}
        />
      ))}
    </div>
  );
}

function VaccineRow({ vax }: { vax: BookingGroupVaccination }) {
  const isExpired = vax.expiresOn && vax.expiresOn < new Date().toISOString().slice(0, 10);
  const statusLabel = !vax.expiresOn
    ? 'No expiry on record'
    : isExpired
      ? `Expired ${vax.expiresOn}`
      : `Valid to ${vax.expiresOn}`;

  return (
    <div className="flex items-center justify-between py-2 border-b border-border last:border-0">
      <div className="flex items-center gap-2">
        <Syringe
          className={`size-3.5 shrink-0 ${vax.valid ? 'text-emerald-600' : 'text-amber-600'}`}
          strokeWidth={1.75}
        />
        <span className="text-sm text-foreground">{vax.vaccineName}</span>
      </div>
      <span
        className={`num text-xs ${vax.valid ? 'text-muted-foreground' : 'font-medium text-amber-700'}`}
      >
        {statusLabel}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function CheckinWorkflowDrawer({
  bookingId,
  entry,
  customer,
  onClose,
  onCheckedIn,
}: CheckinWorkflowDrawerProps) {
  const [step, setStep] = useState(0);
  const [belongings, setBelongings] = useState<BelongingRow[]>([]);
  const [newBelonging, setNewBelonging] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { pet } = entry;
  const vaccinations: BookingGroupVaccination[] = pet.vaccinations ?? [];
  const invalidVaccines = vaccinations.filter((v) => !v.valid);
  const hasVaccineIssues = invalidVaccines.length > 0;

  // --- Belonging helpers ---

  function addBelonging() {
    const desc = newBelonging.trim();
    if (!desc) return;
    setBelongings((prev) => [...prev, { id: `${Date.now()}-${Math.random()}`, description: desc }]);
    setNewBelonging('');
  }

  function removeBelonging(id: string) {
    setBelongings((prev) => prev.filter((b) => b.id !== id));
  }

  // --- Confirm & check in ---

  async function handleConfirm() {
    setBusy(true);
    setError(null);
    try {
      // 1. Persist belonging rows (fire-and-forget failures are acceptable —
      //    staff can add more via BelongingsSection after check-in).
      for (const b of belongings) {
        await createBookingBelonging(bookingId, { description: b.description }).catch(() => void 0);
      }
      // 2. Transition booking to checked_in.
      await staffCheckinBooking(bookingId);
      onCheckedIn();
    } catch (e: unknown) {
      setError((e as Error).message ?? 'Check-in failed');
    } finally {
      setBusy(false);
    }
  }

  // --- Step content ---

  function renderStep() {
    switch (step) {
      case 0:
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Vaccines for {pet.name}</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Review vaccination records before proceeding.
              </p>
            </div>

            {vaccinations.length === 0 ? (
              <p className="rounded-lg border border-border bg-secondary px-4 py-3 text-sm text-muted-foreground">
                No vaccination records on file.
              </p>
            ) : (
              <div className="rounded-lg border border-border bg-background px-4">
                {vaccinations.map((v) => (
                  <VaccineRow key={v.id} vax={v} />
                ))}
              </div>
            )}

            {hasVaccineIssues && (
              <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
                <AlertTriangle
                  className="mt-0.5 size-4 shrink-0 text-amber-600"
                  strokeWidth={1.75}
                />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    {invalidVaccines.length} vaccine issue
                    {invalidVaccines.length > 1 ? 's' : ''} detected
                  </p>
                  <p className="mt-0.5 text-xs text-amber-700">
                    You may proceed, but consider requesting an override from your manager or asking
                    the customer to update records.
                  </p>
                  {pet.id && customer?.id && (
                    <a
                      href={`/customers/${customer.id}/pets/${pet.id}/edit`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:underline"
                    >
                      Update vaccines ↗
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 1:
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Customer Details</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Verify details are correct before proceeding.
              </p>
            </div>

            {customer ? (
              <div className="rounded-lg border border-border bg-background divide-y divide-border">
                <div className="flex items-center gap-3 px-4 py-3">
                  <User className="size-4 shrink-0 text-muted-foreground" strokeWidth={1.75} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground">
                      {customer.firstName} {customer.lastName}
                    </p>
                    {customer.email && (
                      <p className="text-xs text-muted-foreground">{customer.email}</p>
                    )}
                  </div>
                  <a
                    href={`/customers/${customer.id}/edit`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-primary hover:underline shrink-0"
                  >
                    Edit ↗
                  </a>
                </div>
                {customer.phone && (
                  <div className="px-4 py-3">
                    <p className="text-xs text-muted-foreground">Phone</p>
                    <p className="num mt-0.5 text-sm text-foreground">{customer.phone}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No customer on record.</p>
            )}
          </div>
        );

      case 2:
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Record Belongings</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Log items the customer is leaving with {pet.name}.
              </p>
            </div>

            {/* Belonging list */}
            {belongings.length > 0 && (
              <ul className="space-y-1.5">
                {belongings.map((b) => (
                  <li
                    key={b.id}
                    className="flex items-center justify-between rounded-md border border-border bg-background px-3 py-2"
                  >
                    <div className="flex items-center gap-2">
                      <Package
                        className="size-3.5 shrink-0 text-muted-foreground"
                        strokeWidth={1.75}
                      />
                      <span className="text-sm text-foreground">{b.description}</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeBelonging(b.id)}
                      className="rounded p-0.5 text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${b.description}`}
                    >
                      <Trash2 className="size-3.5" strokeWidth={1.75} />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Add row */}
            <div className="flex gap-2">
              <input
                type="text"
                value={newBelonging}
                onChange={(e) => setNewBelonging(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addBelonging();
                  }
                }}
                placeholder="e.g. Lead, toy, food bag…"
                className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm outline-none placeholder:text-muted-foreground focus:border-blue-400 focus:ring-2 focus:ring-blue-500/20"
              />
              <button
                type="button"
                onClick={addBelonging}
                disabled={!newBelonging.trim()}
                className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-secondary disabled:opacity-50"
              >
                <Plus className="size-3.5" strokeWidth={1.75} />
                Add
              </button>
            </div>

            {belongings.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Skip this step if the customer has not brought any belongings.
              </p>
            )}
          </div>
        );

      case 3:
        return (
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-semibold text-foreground">Confirm Check-In</h3>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Review the summary then check in {pet.name}.
              </p>
            </div>

            <div className="rounded-lg border border-border bg-background divide-y divide-border">
              {/* Pet */}
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Pet</p>
                <p className="mt-0.5 text-sm font-semibold text-foreground">
                  {pet.name}
                  {pet.breed ? ` — ${pet.breed}` : ''}
                </p>
              </div>

              {/* Customer */}
              {customer && (
                <div className="px-4 py-3">
                  <p className="text-xs text-muted-foreground">Customer</p>
                  <p className="mt-0.5 text-sm text-foreground">
                    {customer.firstName} {customer.lastName}
                  </p>
                </div>
              )}

              {/* Vaccines */}
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Vaccines</p>
                <p
                  className={`mt-0.5 text-sm ${hasVaccineIssues ? 'text-amber-700 font-medium' : 'text-foreground'}`}
                >
                  {hasVaccineIssues
                    ? `${invalidVaccines.length} issue${invalidVaccines.length > 1 ? 's' : ''} — proceeding with override`
                    : 'All current'}
                </p>
              </div>

              {/* Belongings */}
              <div className="px-4 py-3">
                <p className="text-xs text-muted-foreground">Belongings</p>
                {belongings.length === 0 ? (
                  <p className="mt-0.5 text-sm text-muted-foreground">None recorded</p>
                ) : (
                  <ul className="mt-1 space-y-0.5">
                    {belongings.map((b) => (
                      <li key={b.id} className="text-sm text-foreground">
                        {b.description}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            {error && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  }

  const isLastStep = step === STEPS.length - 1;

  const content = (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-foreground/40 sm:items-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl bg-surface ring-1 ring-border max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4 shrink-0">
          <div className="flex items-center gap-2.5">
            <LogIn className="size-4 text-emerald-600" strokeWidth={1.75} />
            <div>
              <h2 className="text-sm font-semibold text-foreground">Check In</h2>
              <p className="text-xs text-muted-foreground">{STEPS[step]}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-secondary"
            aria-label="Close"
          >
            <X className="size-4" strokeWidth={1.75} />
          </button>
        </div>

        {/* Progress bar */}
        <div className="px-5 pt-3 pb-1 shrink-0">
          <StepIndicator current={step} total={STEPS.length} />
          <p className="mt-1.5 text-xs text-muted-foreground">
            Step {step + 1} of {STEPS.length}
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">{renderStep()}</div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-5 py-4 shrink-0">
          <button
            type="button"
            onClick={() => {
              if (step === 0) onClose();
              else setStep((s) => s - 1);
            }}
            className="flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-2 text-xs font-medium hover:bg-secondary"
          >
            <ChevronLeft className="size-3.5" strokeWidth={1.75} />
            {step === 0 ? 'Cancel' : 'Back'}
          </button>

          {isLastStep ? (
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={busy}
              className="flex items-center gap-1.5 rounded-md bg-emerald-600 px-4 py-2 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-60"
            >
              {busy ? (
                <Loader2 className="size-3.5 animate-spin" strokeWidth={1.75} />
              ) : (
                <CheckCircle className="size-3.5" strokeWidth={1.75} />
              )}
              Check In
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setStep((s) => s + 1)}
              className="flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-xs font-medium text-white hover:bg-blue-700"
            >
              Next
              <ChevronRight className="size-3.5" strokeWidth={1.75} />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(content, document.body);
}
